import { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { Calendar, ChevronDown, ListTodo, X } from '@/components/ui/icons';
import type { Priority } from '@shared/types';
import { getDb, useDb, useDbShallow } from '@/lib/store';
import { effectiveStatuses, spacesOf } from '@/lib/hierarchy';
import { createTask } from '@/lib/actions';
import { useOpenTask, useWorkspaceId } from '@/lib/nav';
import { useUi } from '@/lib/ui';
import { formatDue } from '@/lib/format';
import { Popover } from '@/components/ui/Popover';
import { AvatarStack } from '@/components/ui/Avatar';
import { StatusMenu, StatusPill } from '@/components/fields/Status';
import { PeopleMenu } from '@/components/fields/People';
import { PriorityFlag, PriorityMenu } from '@/components/fields/Priority';
import { DateRangeMenu } from '@/components/fields/DatePicker';
import { TagChip, TagMenu } from '@/components/fields/Tags';
import { TaskTypeMenu } from '@/components/fields/TaskCells';
import { Icon } from '@/components/ui/Icon';
import { RichEditor, type RichEditorHandle } from '@/components/editor/RichEditor';
import { MoveTaskMenuPicker } from './ListPicker';
import { toast } from '@/components/ui/overlays';

function useDefaultListId(): string | undefined {
  const { pathname } = useLocation();
  const ws = useWorkspaceId();
  return useDb((db) => {
    const m = pathname.match(/\/l\/([^/]+)/);
    if (m && db.lists[m[1]]) return m[1];
    const f = pathname.match(/\/f\/([^/]+)/);
    if (f) return Object.values(db.lists).find((l) => l.folderId === f[1])?.id;
    const s = pathname.match(/\/s\/([^/]+)/);
    if (s) return Object.values(db.lists).find((l) => l.spaceId === s[1])?.id;
    const space = spacesOf(db, ws)[0];
    return space ? Object.values(db.lists).find((l) => l.spaceId === space.id)?.id : undefined;
  });
}

export function CreateTaskModal() {
  const ws = useWorkspaceId();
  const openTask = useOpenTask();
  const defaultList = useDefaultListId();
  const [listId, setListId] = useState(defaultList);
  const list = useDb((db) => (listId ? db.lists[listId] : undefined));
  const statuses = useDbShallow((db) => (listId ? effectiveStatuses(db, listId) : []));
  const [name, setName] = useState('');
  const [statusId, setStatusId] = useState<string | undefined>(undefined);
  const [assignees, setAssignees] = useState<string[]>([]);
  const [start, setStart] = useState<number | null>(null);
  const [due, setDue] = useState<number | null>(null);
  const [priority, setPriority] = useState<Priority | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  const [typeId, setTypeId] = useState<string | null>(null);
  const taskType = useDb((db) => (typeId ? db.taskTypes[typeId] : Object.values(db.taskTypes).find((t) => t.builtin === 'task')));
  const descRef = useRef<RichEditorHandle>(null);
  const close = () => useUi.setState({ createTaskOpen: false });
  const status = useMemo(() => statuses.find((s) => s.id === statusId) ?? statuses[0], [statuses, statusId]);

  const submit = (openAfter = false) => {
    if (!name.trim() || !listId) return;
    const t = createTask({
      listId,
      name,
      statusId: status?.id,
      assigneeIds: assignees,
      startDate: start,
      dueDate: due,
      priority,
      tagIds: tags,
      taskTypeId: typeId,
      description: descRef.current?.isEmpty() ? '' : (descRef.current?.getHTML() ?? ''),
    });
    close();
    if (openAfter) openTask(t.id);
    else toast(`Task created in ${getDb().lists[listId]?.name}`, { label: 'Open', onClick: () => openTask(t.id) });
  };

  return createPortal(
    <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && close()}>
      <div className="modal create-task" style={{ width: 680 }}>
        <div className="ct-head">
          <span className="ct-tab on">Task</span>
          <span className="spacer" />
          <button className="icon-btn sm" onClick={close}>
            <X size={16} />
          </button>
        </div>
        <div className="ct-loc">
          <Popover content={(c) => <MoveTaskMenuPicker onPick={(id) => (setListId(id), setStatusId(undefined), c())} />}>
            <button className="chip">
              <ListTodo size={13} /> {list?.name ?? 'Select List'} <ChevronDown size={12} />
            </button>
          </Popover>
          <Popover content={(c) => <TaskTypeMenu workspaceId={ws} value={typeId} onPick={(id) => (setTypeId(id), c())} />}>
            <button className="chip">
              <Icon name={taskType?.icon ?? 'CircleDot'} size={13} /> {taskType?.name ?? 'Task'} <ChevronDown size={12} />
            </button>
          </Popover>
        </div>
        <input
          className="ct-name"
          autoFocus
          placeholder="Task Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit(e.ctrlKey || e.metaKey);
            if (e.key === 'Escape') close();
          }}
        />
        <div className="ct-desc">
          <RichEditor ref={descRef} content="" placeholder="Add description, or type '@' to mention" />
        </div>
        <div className="ct-props">
          {status && (
            <Popover content={(c) => <StatusMenu statuses={statuses} currentId={status.id} onPick={(s) => (setStatusId(s.id), c())} />}>
              <button className="ct-prop">
                <StatusPill status={status} statuses={statuses} size="sm" />
              </button>
            </Popover>
          )}
          <Popover content={() => <PeopleMenu selected={assignees} onChange={setAssignees} />}>
            <button className="ct-prop">{assignees.length ? <AvatarStack userIds={assignees} size={20} /> : <>Assignee</>}</button>
          </Popover>
          <Popover content={(c) => <DateRangeMenu start={start} due={due} onChange={(s, d) => (setStart(s), setDue(d))} onClose={c} />}>
            <button className="ct-prop">
              <Calendar size={13} /> {due ? formatDue(due) : 'Due date'}
            </button>
          </Popover>
          <Popover content={(c) => <PriorityMenu value={priority} onPick={(p) => (setPriority(p), c())} />}>
            <button className="ct-prop">
              <PriorityFlag priority={priority} showLabel /> {!priority && 'Priority'}
            </button>
          </Popover>
          {list && (
            <Popover content={() => <TagMenu spaceId={list.spaceId} selected={tags} onChange={setTags} />}>
              <button className="ct-prop">{tags.length ? tags.map((t) => <TagChip key={t} tagId={t} small />) : 'Tags'}</button>
            </Popover>
          )}
        </div>
        <div className="modal-footer">
          <span className="faint" style={{ fontSize: 'var(--fs-sm)', marginRight: 'auto' }}>
            Enter to create · Ctrl+Enter to create & open
          </span>
          <button className="btn primary" disabled={!name.trim() || !listId} onClick={() => submit(false)}>
            Create Task
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
